import {
    Box,
    Button,
    Typography,
    Grid,
    TextField,
    MenuItem,
    CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import DateTimePickerComponent from '../../../components/DateTimePickerComponent';
import {
    useAsignarResultadoExamenEmpleadoMutation,
    useGetAptitudesQuery
} from '../../../services/periodicosApi';
import UploadFileIcon from '@mui/icons-material/UploadFile';

interface PeriodicosDrawerProps {
    selectedIds: string[];
    setOpen: (state: string) => void;
    reloadGrid: () => void;
    selectedEmployee: any;
}

const PeriodicosAsignarResultadoDrawer = ({
    selectedIds,
    setOpen,
    reloadGrid,
    selectedEmployee
}: PeriodicosDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const { enqueueSnackbar } = useSnackbar();
    const [archivoResultado, setArchivoResultado] = useState<File | null>(null);
    const [fechaResultado, setFechaResultado] = useState<any>(null);
    const [idAptitud, setIdAptitud] = useState<number | null>(null);
    const [observaciones, setObservaciones] = useState<string>('');

    const idEstadoEmpleadoExpuesto = selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto;
    const isReadOnly = idEstadoEmpleadoExpuesto === 5;

    useEffect(() => {
        if (isReadOnly) {
            const fecha = selectedEmployee?.examenEmpleado?.fechaResultado;
            const aptitudId = selectedEmployee?.examenEmpleado?.aptitud?.id;
            const obs = selectedEmployee?.examenEmpleado?.observacion;

            if (fecha) setFechaResultado(dayjs(fecha));
            if (aptitudId) setIdAptitud(aptitudId);
            if (obs) setObservaciones(obs);
        }
    }, [selectedEmployee]);

    const { data: aptitudes = [] } = useGetAptitudesQuery();
    const [asignarResultado, { isLoading }] = useAsignarResultadoExamenEmpleadoMutation();

    const handleCloseDrawer = () => setOpen('');

    const handleSave = async () => {
        const idExamenEmpleado = parseInt(selectedIds[0].split('-')[0], 10);
        if (idAptitud === null || !fechaResultado) return;

        const payload = {
            idAptitud,
            fechaResultado: dayjs(fechaResultado).toISOString(),
            observaciones,
            archivoResultado
        };

        try {
            await asignarResultado({ idExamenEmpleado, payload }).unwrap();
            enqueueSnackbar(t('drawer.messages.assignSuccessResults'), { variant: 'success' });
            reloadGrid();
            setOpen('');
        } catch (error) {
            enqueueSnackbar(t('drawer.messages.assignErrorResults'), { variant: 'error' });
        }
    };

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Grid container flexDirection="column">
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text={` ${selectedEmployee?.examenEmpleado?.empleado?.nombre || ''} - ${
                        selectedEmployee?.examenEmpleado?.riesgo?.codigo || ''
                    } ${selectedEmployee?.examenEmpleado?.riesgo?.descripcion || ''}`}
                />
                <Typography sx={{ fontWeight: 'bold', mb: 2, mt: 4 }}>
                    {t('drawer.exam.subtitle')}
                </Typography>

                <Box>
                    <Typography variant="body1">
                        {selectedEmployee?.examenEmpleado?.examen?.descripcion || '-'}
                    </Typography>
                </Box>

                <Typography sx={{ fontWeight: 'bold', mt: 4 }}>
                    {t('drawer.resultado.title')}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} md={6}>
                        <DateTimePickerComponent
                            label={t('drawer.resultado.date')}
                            value={fechaResultado}
                            onChange={setFechaResultado}
                            disableFuture
                            fullWidth
                            disabled={isReadOnly}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            label={t('drawer.resultado.aptitud')}
                            value={idAptitud ?? ''}
                            onChange={(e) => setIdAptitud(Number(e.target.value))}
                            fullWidth
                            disabled={isReadOnly}
                        >
                            {aptitudes.map((aptitud) => (
                                <MenuItem key={aptitud.id} value={aptitud.id}>
                                    {aptitud.nombre}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sx={{ m: 0.5, p: 0 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            startIcon={<UploadFileIcon />}
                            sx={{ p: 2, borderRadius: '5px', alignItems: 'initial' }}
                            disabled={isReadOnly}
                        >
                            {isReadOnly
                                ? t('drawer.result.uploaded')
                                : archivoResultado
                                ? archivoResultado.name
                                : t('drawer.result.upload')}
                            <input
                                type="file"
                                accept="application/pdf"
                                hidden
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file && file.type === 'application/pdf') {
                                        setArchivoResultado(file);
                                    }
                                }}
                            />
                        </Button>
                    </Grid>
                </Grid>

                <TextField
                    label={t('drawer.resultado.observations')}
                    multiline
                    rows={3}
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    fullWidth
                    sx={{ mt: 3, width: '300px' }}
                    disabled={isReadOnly}
                />
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={handleCloseDrawer} sx={{ mr: 1 }}>
                    {t('drawer.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={isReadOnly || !fechaResultado || !idAptitud || isLoading}
                    sx={{ textTransform: 'none', minWidth: 100 }}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('drawer.save')}
                </Button>
            </Box>
        </Box>
    );
};

export default PeriodicosAsignarResultadoDrawer;
